// components/create/MoreSection.js
import React, { useState } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiX } from 'react-icons/fi';
import '../../styles/MoreSection.scss';

const PREDEFINED_FIELDS = [
  { id: 'email',     label: 'Email Address' },
  { id: 'instagram', label: 'Instagram Handle' },
  { id: 'twitter',   label: 'Twitter/X Handle' },
  { id: 'snapchat',  label: 'Snapchat Username' },
  { id: 'linkedin',  label: 'LinkedIn Profile' },
];

// Returns a simple unique ID
const uid = () => `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

export default function MoreSection({
  additionalInfoFields,
  setAdditionalInfoFields,
  eventId,
  financialYear,
  linkedCommunity,
  setLinkedCommunity,
  agreedToTerms,
  setAgreedToTerms,
}) {
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [editingField, setEditingField] = useState(null); // null = new
  const [customLabel, setCustomLabel] = useState('');
  const [customRequired, setCustomRequired] = useState(false);
  const [labelError, setLabelError] = useState('');

  // ── helpers ──────────────────────────────────────────────────────────────
  const isPredefinedActive = (id) =>
    additionalInfoFields.some(f => (typeof f === 'string' ? f : f.id) === id);

  const togglePredefined = (id) => {
    if (isPredefinedActive(id)) {
      setAdditionalInfoFields(additionalInfoFields.filter(
        f => (typeof f === 'string' ? f : f.id) !== id
      ));
    } else {
      setAdditionalInfoFields([...additionalInfoFields, id]);
    }
  };

  const customFields = additionalInfoFields.filter(f => typeof f !== 'string');

  const openNewCustom = () => {
    setEditingField(null);
    setCustomLabel('');
    setCustomRequired(false);
    setLabelError('');
    setShowCustomModal(true);
  };

  const openEditCustom = (field) => {
    setEditingField(field);
    setCustomLabel(field.label);
    setCustomRequired(field.required || false);
    setLabelError('');
    setShowCustomModal(true);
  };

  const handleSaveCustom = () => {
    const trimmed = customLabel.trim();
    if (!trimmed) { setLabelError('Question text is required'); return; }

    if (editingField) {
      setAdditionalInfoFields(additionalInfoFields.map(f =>
        (typeof f !== 'string' && f.id === editingField.id)
          ? { ...f, label: trimmed, required: customRequired }
          : f
      ));
    } else {
      setAdditionalInfoFields([...additionalInfoFields, {
        id: uid(),
        label: trimmed,
        type: 'text',
        required: customRequired,
      }]);
    }
    setShowCustomModal(false);
  };

  const deleteCustom = (id) => {
    setAdditionalInfoFields(additionalInfoFields.filter(
      f => !(typeof f !== 'string' && f.id === id)
    ));
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="more-section">

      {/* Additional Info Fields */}
      <div className="section-card">
        <label className="section-title">Additional Info Fields</label>
        <p className="section-description">
          Collect extra info from guests during RSVP
        </p>

        {/* Predefined toggles */}
        <div className="predefined-fields">
          {PREDEFINED_FIELDS.map(pf => (
            <button
              key={pf.id}
              type="button"
              className={`predefined-chip ${isPredefinedActive(pf.id) ? 'active' : ''}`}
              onClick={() => togglePredefined(pf.id)}
            >
              {pf.label}
              {isPredefinedActive(pf.id) && <FiX size={13} style={{ marginLeft: 4 }} />}
            </button>
          ))}
        </div>

        {/* Custom questions list */}
        {customFields.length > 0 && (
          <div className="custom-fields-list">
            {customFields.map(field => (
              <div key={field.id} className="field-item">
                <div className="field-content">
                  <p className="field-label">{field.label}</p>
                  <p className="field-type">{field.required ? 'Required' : 'Optional'} · Custom</p>
                </div>
                <div className="field-actions">
                  <button type="button" className="icon-button" onClick={() => openEditCustom(field)}>
                    <FiEdit2 size={16} />
                  </button>
                  <button type="button" className="icon-button delete" onClick={() => deleteCustom(field.id)}>
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <button type="button" className="add-button" onClick={openNewCustom}>
          <FiPlus size={16} />
          <span>Add Custom Question</span>
        </button>
      </div>

      {/* Community Link */}
      <div className="section-card">
        <label className="section-title">Link to Community (Optional)</label>
        <p className="section-description">Connect this event to one of your communities</p>
        {linkedCommunity ? (
          <div className="linked-community">
            <div className="community-info">
              <p className="community-name">{linkedCommunity.name}</p>
              <p className="community-members">{linkedCommunity.member_count} members</p>
            </div>
            <button type="button" className="icon-button" onClick={() => setLinkedCommunity(null)}>
              <FiX size={16} />
            </button>
          </div>
        ) : (
          <button type="button" className="select-button" onClick={() => {}}>
            <span>Select Community</span>
          </button>
        )}
      </div>

      {/* Event Stats (edit mode) */}
      {eventId && (
        <div className="section-card">
          <label className="section-title">Event Statistics</label>
          <div className="stats-grid">
            <div className="stat-item">
              <p className="stat-label">Event ID</p>
              <p className="stat-value">{eventId}</p>
            </div>
            {financialYear && (
              <div className="stat-item">
                <p className="stat-label">Financial Year</p>
                <p className="stat-value">{financialYear}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Terms & Conditions */}
      <div className="section-card">
        <label className="section-title">Terms & Conditions</label>
        <div className="terms-content">
          <p>By creating this event, you agree to:</p>
          <ul>
            <li>Provide accurate and truthful information</li>
            <li>Comply with local laws and regulations</li>
            <li>Respect the privacy of your guests</li>
            <li>Our <a href="/terms-and-conditions" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a></li>
          </ul>
        </div>
        <label className="checkbox-container">
          <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />
          <span className="checkmark"></span>
          <span className="checkbox-label">I agree to the terms and conditions</span>
        </label>
      </div>

      {/* Tips */}
      <div className="section-card tips-card">
        <label className="section-title">💡 Tips for a Great Event</label>
        <ul className="tips-list">
          <li>Use a clear, eye-catching cover image</li>
          <li>Provide detailed location information</li>
          <li>Set realistic RSVP deadlines</li>
          <li>Respond to guest questions promptly</li>
          <li>Update event details if plans change</li>
        </ul>
      </div>

      {/* Custom Question Modal */}
      {showCustomModal && (
        <div className="modal-overlay" onClick={() => setShowCustomModal(false)}>
          <div className="custom-question-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingField ? 'Edit Question' : 'Add Custom Question'}</h3>
              <button className="close-btn" onClick={() => setShowCustomModal(false)}>
                <FiX size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Question Text <span className="required">*</span></label>
                <input
                  type="text"
                  className={`text-input ${labelError ? 'error' : ''}`}
                  placeholder="e.g. What are your dietary requirements?"
                  value={customLabel}
                  onChange={e => { setCustomLabel(e.target.value); setLabelError(''); }}
                  maxLength={200}
                  autoFocus
                />
                {labelError && <span className="field-error">{labelError}</span>}
              </div>

              <label className="required-toggle">
                <input
                  type="checkbox"
                  checked={customRequired}
                  onChange={e => setCustomRequired(e.target.checked)}
                />
                <span>Required question</span>
              </label>
            </div>

            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowCustomModal(false)}>Cancel</button>
              <button className="submit-btn" onClick={handleSaveCustom}>
                {editingField ? 'Save Changes' : 'Add Question'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
